import ReactPlayer from "react-player";
import { Image } from "primereact/image";
import moment from "moment";
import { FaGlobeAmericas } from "react-icons/fa";
import { HiSaveAs } from "react-icons/hi";
import { Button } from "primereact/button";
import { saveAs } from "file-saver";
import Linkify from "react-linkify";
import { RWebShare } from "react-web-share";
import { Badge } from "primereact/badge";
import { AiFillLike, AiOutlineLike } from "react-icons/ai";

// globals
import { FEEDTYPE } from "../../constants/FEEDTYPE";
import { defaultImage } from "../../constants/IMAGES";
import { useSelector } from "react-redux";
import React from "react";
import { Translate } from "react-auto-translate";

const PostItem = (
  {
    item,
    currentUserId,
    onEditPost,
    onDeletePost,
    onAvatarClick,
    onLikeClick,
    onCommentClick,
    shareData,
    liked = false,
  },
  key
) => {
  // console.table(item);

  const authenticateState = useSelector((state) => state.authenticate);
  const [isLiked, setIsLiked] = React.useState(liked);
  return (
    <div key={key} className="bg-white border-round-lg pt-4">
      {/*SECTION ONE: USER DETAILS, TIME POSTED AND ACTIONS */}
      {/* ---------------------------------------------------------------- */}
      <div className="flex align-items-start justify-content-between px-2 md:px-5">
        {/* image block */}
        <div className="flex align-items-center justify-content-between gap-2">
          <Image
            src={
              item?.userId === currentUserId
                ? authenticateState.user.profileImage ?? defaultImage
                : item?.createdBy?.profileImage ?? defaultImage
            }
            imageClassName="border-circle h-3rem w-3rem"
            imageStyle={{
              objectFit: "cover",
            }}
            onClick={onAvatarClick}
          />
          <div className="flex flex-column text-color">
            <div className="w-full text-base">
              {item?.userId === currentUserId
                ? authenticateState.user.name
                : item?.createdBy?.name}{" "}
              <Badge
                className={`${
                  item.createdBy?.userType === "entrepreneur"
                    ? "bg-primary-100"
                    : item.createdBy?.userType === "investor"
                    ? "bg-blue-100"
                    : "bg-cyan-100"
                } font-light text-gray-800`}
                value={
                  <Translate>
                    {item.createdBy?.userType?.replace(/_+/g, " ")}
                  </Translate>
                }
              />{" "}
            </div>
            <div className="text-xs flex align-items-center gap-1 mt-1">
              <Translate>
                {moment(new Date(item?.createdAt)).fromNow()}
              </Translate>{" "}
              <FaGlobeAmericas />
            </div>
          </div>
        </div>

        {/* action block, shows only for current logged in user*/}
        {item?.userId === currentUserId && (
          <div className="flex gap-1 align-items-center">
            <Button
              icon="pi pi-pencil"
              className="p-button-sm p-button-text"
              onClick={onEditPost}
            />
            <Button
              icon="pi pi-trash"
              className="p-button-sm p-button-text"
              onClick={onDeletePost}
            />
          </div>
        )}
      </div>
      {/* ---------------------------------------------------------------- */}

      {/* SECTION TWO: DESCRIPTION */}
      {/* ---------------------------------------------------------------- */}
      <div className="mt-3 mb-1 px-3 md:px-5">
        <Linkify
          //customize the whole component
          componentDecorator={(decoratedHref, decoratedText) => (
            <a
              href={decoratedHref}
              target={"_blank"}
              rel="noreferrer"
              className="no-underline"
            >
              {decoratedText}
            </a>
          )}
          //link url
          hrefDecorator={(urlHref) => urlHref}
          //link text
          textDecorator={(text) => {
            function addHttps(value) {
              if (!/^(f|ht)tps?:\/\//i.test(value)) {
                value = "https://" + value;
              }
              return value;
            }

            return (
              <span className="text-blue-500 font-bold underline">
                {new URL(addHttps(text))?.hostname}
              </span>
            );
          }}
        >
          {/* render text with space and paragraphs */}
          {item?.description?.split("\n").map((paragraph, index) => (
            <p key={index} className="text-color">
              {paragraph}
            </p>
          ))}
        </Linkify>
      </div>

      {/* ---------------------------------------------------------------- */}

      {/*SECTION THREE: RENDER ON POST TYPE  */}
      {/* ---------------------------------------------------------------- */}
      <div className="w-full flex mx-auto">
        {item.fileData?.type === FEEDTYPE.IMAGE ? (
          <ImageType file={item?.fileUrl} />
        ) : item.fileData?.type === FEEDTYPE.VIDEO ? (
          <VideoType file={item?.fileUrl} />
        ) : item.fileData?.type === FEEDTYPE.DOCUMENT ? (
          <DocumentType
            documentName={item?.fileData.name}
            onDownload={() => saveAs(item?.fileUrl, item?.documentName)}
          />
        ) : (
          <GeneralType />
        )}
      </div>

      {/* ---------------------------------------------------------------- */}

      {/*SECTION 4 LIKES AND COMMENTS*/}
      {/* ---------------------------------------------------------------- */}
      <div className="flex flex-column mt-3 border-gray-200">
        <div className="flex justify-content-between px-5 mt-2">
          <span className="flex gap-2 align-items-center">
            {item?.likeCount ?? 0} <Translate>Likes</Translate>
          </span>

          <span
            className="hover:underline cursor-pointer"
            onClick={onCommentClick}
          >
            {item?.commentCount ?? 0} <Translate>Comments</Translate>
          </span>
        </div>
        <div className="border-top-1 border-gray-200 py-1 mt-2 flex justify-content-center align-items-center gap-5">
          <Button
            className="p-button-sm p-button-text lg:text-lg 
          flex justify-content-center gap-2 text-color px-5"
            onClick={() => {
              setIsLiked((value) => {
                onLikeClick(!value);
                return !value;
              });
            }}
          >
            {isLiked ? (
              <AiFillLike className="text-primary" size={20} />
            ) : (
              <AiOutlineLike size={20} />
            )}{" "}
            <span className="hidden md:block">
              <Translate>Like</Translate>
            </span>
          </Button>
          <Button
            className="p-button-sm p-button-text lg:text-lg 
          flex justify-content-center gap-2 text-color px-1 md:px-5"
            onClick={onCommentClick}
          >
            <i className="pi pi-comment" />{" "}
            <span className="hidden md:block">
              <Translate>Comment</Translate>
            </span>
          </Button>

          <RWebShare
            data={shareData}
            onClick={() => console.log("shared successfully!")}
          >
            <Button
              className="p-button-sm p-button-text lg:text-lg 
          flex justify-content-center gap-2 text-color px-5"
            >
              <i className="pi pi-share-alt" />{" "}
              <span className="hidden md:block">
                <Translate>Share</Translate>
              </span>
            </Button>
          </RWebShare>
        </div>
      </div>
      {/* ---------------------------------------------------------------- */}
    </div>
  );
};

function ImageType({ file }) {
  return (
    <div className="w-full mt-2 flex justify-content-center bg-gray-800 align-items-center">
      <Image
        src={file}
        imageStyle={{
          aspectRatio: "4/3",
          width: "100%",
          objectFit: "contain",
          backgroundColor: "rgba(10, 58, 90, .2)",
        }}
        alt="post"
        preview
        downloadable
      />
    </div>
  );
}

function VideoType({ file }) {
  return (
    <div className="mt-2 w-full">
      <ReactPlayer
        url={file}
        controls={true}
        width="100%"
        volume={0}
        loop={false}
        muted={true}
        autoPlay={true}
        style={{
          backgroundColor: "rgba(10, 58, 90, .2)",
          objectFit: "cover",
        }}
      />
    </div>
  );
}

function DocumentType({ documentName, onDownload }) {
  return (
    <div className="flex justify-content-center  mx-auto">
      <Button
        className="p-button-text p-button-sm p-button-raised flex align-items-center 
        justify-content-center text-color px-5 gap-3"
        onClick={onDownload}
      >
        <Image
          src="https://www.svgrepo.com/show/375299/word-document.svg"
          imageClassName="h-2rem w-2rem"
        />
        <div className="max-w-7rem lg:max-w-30rem white-space-nowrap overflow-hidden text-overflow-ellipsis">
          {documentName}
        </div>
        <HiSaveAs size={20} />
      </Button>
    </div>
  );
}

function GeneralType() {}

export default PostItem;
